// 全局路由权限守卫：处理登录态校验、动态路由注入，以及统一的 Office 登录落地。
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";

import NProgress from "@/utils/nprogress";
import { TOKEN_KEY } from "@/enums/CacheEnum";
import router from "@/router";
import { routePermission } from "@/permission";
import { resolveDefaultHomePath } from "@/permission/role-access";
import { usePermissionStore, useUserStore } from "@/store";

/**
 * 登录后统一进入 Office 工作台，忽略登录页携带的历史 redirect。
 * @param to 本次目标路由
 * @param from 本次来源路由
 * @returns 需改道的 path；null 表示沿用当前 to
 */
function resolveLandingPath(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): string | null {
  const defaultHome = resolveDefaultHomePath();
  const fromLogin = from.path === "/login";

  if (!fromLogin || to.path === defaultHome) return null;
  return defaultHome;
}

/**
 * 权限初始化完成后，判断是否应改道至角色默认首页或合法 redirect 目标。
 * @returns true 表示已调用 next，调用方应 return
 */
function applyLandingOrDeny(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  options: { replayTarget?: boolean } = {}
): boolean {
  const landingPath = resolveLandingPath(to, from);
  if (landingPath) {
    next({ path: landingPath, replace: true });
    return true;
  }
  if (hasPermissionKey(to) && !canAccessByPermission(to)) {
    next("/401");
    return true;
  }
  if (options.replayTarget) {
    next({
      path: to.path,
      query: to.query,
      hash: to.hash,
      replace: true,
    });
    return true;
  }
  return false;
}

export function setupPermission() {
  // 白名单路由
  const whiteList = ["/login"];

  router.beforeEach(async (to, from, next) => {
    NProgress.start();
    const hasToken = localStorage.getItem(TOKEN_KEY);

    if (hasToken) {
      if (to.path === "/login") {
        const userStore = useUserStore();
        const roles = userStore.user.roles || [];
        if (roles.length) {
          routePermission.init(roles);
          next({ path: resolveDefaultHomePath(), replace: true });
        } else {
          next({ path: "/" });
        }
        NProgress.done();
      } else {
        const userStore = useUserStore();
        const hasRoles =
          userStore.user.roles && userStore.user.roles.length > 0;

        if (hasRoles) {
          routePermission.init(userStore.user.roles || []);
          if (applyLandingOrDeny(to, from, next)) {
            NProgress.done();
            return;
          }
          // 如果未匹配到任何路由，跳转到404页面
          if (to.matched.length === 0) {
            console.warn(`[router] 未匹配路由: ${to.fullPath}`);
            next({
              path: resolveDefaultHomePath(),
              replace: true,
            });
            NProgress.done();
            return;
          } else if (hasPermissionKey(to) && !canAccessByPermission(to)) {
            next("/401");
          } else {
            // 如果路由参数中有 title，覆盖路由元信息中的 title
            const title =
              (to.params.title as string) || (to.query.title as string);
            if (title) {
              to.meta.title = title;
            }
            next();
          }
        } else {
          const permissionStore = usePermissionStore();
          try {
            await userStore.getUserInfo();
            routePermission.init(userStore.user.roles || []);
            const dynamicRoutes = await permissionStore.generateRoutes();
            dynamicRoutes.forEach((route: RouteRecordRaw) =>
              router.addRoute(route)
            );
            // 动态路由注册后重新解析目标，redirect 权限校验才准确
            const resolvedTo = router.resolve(
              to.fullPath
            ) as RouteLocationNormalized;
            if (
              applyLandingOrDeny(resolvedTo, from, next, { replayTarget: true })
            ) {
              NProgress.done();
              return;
            }
          } catch (error) {
            // 移除 token 并重定向到登录页，携带当前页面路由作为跳转参数
            await userStore.resetToken();
            redirectToLogin(to, next);
            NProgress.done();
          }
        }
      }
    } else {
      // 未登录
      if (whiteList.includes(to.path)) {
        next();
      } else {
        // 不在白名单，重定向到登录页
        redirectToLogin(to, next);
        NProgress.done();
      }
    }
  });

  router.afterEach(() => {
    NProgress.done();
  });
}

/** 重定向到登录页 */
function redirectToLogin(
  to: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const params = new URLSearchParams(to.query as Record<string, string>);
  const queryString = params.toString();
  const redirect = queryString ? `${to.path}?${queryString}` : to.path;
  next(`/login?redirect=${encodeURIComponent(redirect)}`);
}

/** 判断是否有权限（按钮侧对齐老项目：走静态 permissionKey，不再读后端 perms） */
export function hasAuth(
  value: string | string[],
  type: "button" | "role" = "button"
) {
  const { roles } = useUserStore().user;

  if (type === "role") {
    return typeof value === "string"
      ? roles.includes(value)
      : value.some((role) => roles.includes(role));
  }

  // 超管直通（与 RoutePermissionManager 一致）
  if (roles.includes("ROOT") || roles.includes("admin")) {
    return true;
  }

  return typeof value === "string"
    ? routePermission.has(value)
    : routePermission.hasAny(value);
}

/** 路由是否配置了 permissionKey（无 key 则放行，兼容未标注模块） */
function hasPermissionKey(to: RouteLocationNormalized): boolean {
  if (to.meta?.permissionKey) {
    return true;
  }
  return to.matched.some((record) => Boolean(record.meta?.permissionKey));
}

/** 取最深一层带 permissionKey 的匹配记录做鉴权，避免父级模块 key 误伤子页 */
function canAccessByPermission(to: RouteLocationNormalized): boolean {
  for (let i = to.matched.length - 1; i >= 0; i -= 1) {
    const record = to.matched[i];
    if (record.meta?.permissionKey) {
      return routePermission.canAccess(to.path, record);
    }
  }
  return routePermission.canAccess(to.path, to);
}
